import * as React from 'react';
import styled from 'styled-components';
import { ProductsContext } from 'Jsx/ContextApis/Products';
import { withTranslation, WithTranslation } from 'react-i18next';

import {
  primaryColor,
  purple,
  grayLight,
  white,
} from 'crehana-ui/styles/colors';
import InverseButton from 'crehana-ui/Buttons/InverseButton';
import { InverseButtonProps } from 'crehana-ui/Buttons/InverseButton/types';
import userActivity from 'Utils/userActivity';
import {
  eventSources,
  eventNames,
  setFbEventToLocalStorage,
} from 'Jsx/Utils/facebookTracking/facebookTracking';
import mixpanel from 'Jsx/Utils/mixpanelTracking';
import ShoppingCartAdded from 'crehana-icons/ShoppingCartAdded';
import ShoppingCartLight from 'crehana-icons/ShoppingCartLight';

import Check from 'crehana-icons/Check';

import EVENTS from 'Jsx/Shared/Tracking/events';
import { CourseCardProps } from 'Jsx/Shared/CourseCardV3/types';

import buyProduct, { trackProduct } from 'Jsx/Utils/buyProduct';

const StyledButton = styled<React.FC<InverseButtonProps & { rel?: string }>>(
  ({ size, full, className, label, ...rest }) => (
    <InverseButton
      size={size}
      full={full}
      className={className}
      label={label}
      white={white}
      {...rest}
    />
  ),
)``;

StyledButton.defaultProps = {
  className: 'mt-16',
  rel: 'no-follow',
  full: false,
  size: 'xs',
};

const Wrapper = styled.div`
  & .icon-button {
    width: 48px;
    height: 48px;
  }
  & .buy-button {
    width: calc(100% - 56px);
  }
`;

const ButtonWithIcon = styled.button<{ isChecked: boolean; white: boolean }>`
  ${props => {
    if (props.white) {
      return `border: 1px solid ${white}`;
    }
    if (props.isChecked) {
      return `
        border: 1px solid ${grayLight};
      `;
    }
    return `border: 1px solid ${purple}`;
  }}
`;

const AddToCartMobile: React.FC<Partial<CourseCardProps> &
  WithTranslation> = props => {
  const {
    originalId,
    title,
    itemTypeTrack,
    addCourseToCart,
    removeCourseFromCart,
    inShoppingCart,
    prices,
    user,
    isEnrollment,
    urlToBuyNow,
    canEnroll,
    courseCardPage,
    pageType,
    courseType,
    enrollmentAccessPreviewUrl,
    t,
    onClickBuyNow,
    onClickEnrollNow,
    showOnlyAddToCardButton,
    product,
    beforeBuy,
    isBundle,
    enrollmentUrl,
    url,
    dark,
  } = props;

  const handleDirectBuy = () => {
    if (beforeBuy) {
      beforeBuy();
    }
    const fbTrackPayload = {
      content_name: title,
      content_ids: originalId,
      content_type: 'course',
      value: prices?.price,
      currency: prices?.currencySymbol,
    };

    setTimeout(() => {
      if (onClickBuyNow) {
        trackProduct(product);
        onClickBuyNow({
          itemData: {
            itemId: originalId || 0,
            itemType: 'course',
          },
        });
      } else if (product && urlToBuyNow) {
        setFbEventToLocalStorage({
          eventName: eventNames.GO_TO_CHECKOUT,
          sourcePage: eventSources.COURSE_CARD,
          fbTrackPayload,
        });
        buyProduct(urlToBuyNow, product);
      } else {
        setFbEventToLocalStorage({
          eventName: eventNames.GO_TO_CHECKOUT,
          sourcePage: eventSources.COURSE_CARD,
          fbTrackPayload,
        });
        userActivity.track(
          userActivity.EVENTS.CLICK,
          {
            item_id: originalId,
            item_name: `CourseCardV3, ${title}`,
            item_type: 'course',
            item_path: location.pathname,
          },
          urlToBuyNow,
        );
      }
    }, 0);
  };

  const buyUrl = `/shopping-cart/course/${originalId}`;
  const courseEnrollmentUrl =
    (prices && prices.price === 0) || canEnroll
      ? `/enrollment/${originalId}/`
      : buyUrl;
  const classesUrl = `/clases/${originalId}/`;

  const getUrl = () => {
    if (isBundle) {
      return enrollmentUrl;
    }
    if (canEnroll || (prices && prices.price === 0)) {
      return courseEnrollmentUrl;
    }
    return urlToBuyNow;
  };

  const getMixPanelData = () => {
    const abTest =
      typeof window !== 'undefined' &&
      window.__INITIAL_STATE__ &&
      window.__INITIAL_STATE__.ab_tests;

    return {
      itemType: itemTypeTrack || 'COURSE',
      currentPage: courseCardPage,
      originalId,
      title,
      /*
          TODO: move abTest data to mixPanel options (mixpanel.html)
        */
      ...(abTest
        ? Object.keys(abTest).reduce((obj, next) => {
            obj[`ab_test_${next}`] = abTest[next]; // eslint-disable-line no-param-reassign

            return obj;
          }, {})
        : {}),
    };
  };

  const handleBuy = e => {
    mixpanel.track(EVENTS.COURSE_CARD.BUY_NOW, {
      currentPage: courseCardPage,
    });
    if (prices && prices.price === 0) {
      if (!user) {
        e.preventDefault();
        window.nextUrl = isBundle ? enrollmentUrl : courseEnrollmentUrl;
        window.showRegisterModal();
      } else {
        mixpanel.track(EVENTS.COURSE_CARD.BUY_NOW, {
          currentPage: courseCardPage,
        });
        window.location.href = getUrl() || '';
      }
    } else if (!courseCardPage || courseCardPage !== 'CAMPAIGN_LANDINGS') {
      handleDirectBuy();
    } else {
      window.location.href = getUrl() || '';
    }
  };

  const removeCourseFromCartWithTrack = () => {
    const mixPanelData = getMixPanelData();

    mixpanel.track(EVENTS.REMOVE_FROM_CART, mixPanelData);
    removeCourseFromCart({
      courseId: originalId,
    });
  };

  const addCourseToCartWithTrack = () => {
    const mixPanelData = getMixPanelData();

    mixpanel.track(EVENTS.ADD_TO_CART, mixPanelData);
    addCourseToCart({
      courseId: originalId,
    });
  };

  const handleAddToCart = () => {
    if (inShoppingCart) {
      return removeCourseFromCartWithTrack();
    }
    if (addCourseToCart) {
      return addCourseToCartWithTrack();
    }

    window.location.href = buyUrl;
  };

  const styledCart = (
    <Wrapper className="flex justify-between items-center mt-16 w-full">
      <ButtonWithIcon
        isChecked={inShoppingCart}
        className="flex items-center justify-center mr-12 h-48 w-48"
        onClick={() => handleAddToCart()}
        data-testid="add-to-cart"
        white={dark}
      >
        {!inShoppingCart ? (
          <ShoppingCartLight size={20} color={dark ? white : primaryColor} />
        ) : (
          <Check size={16} color={dark ? white : primaryColor} />
        )}
      </ButtonWithIcon>
      <StyledButton
        className="buy-button"
        onClick={e => handleBuy(e)}
        size="m"
        label={t('BUY_NOW', {
          defaultValue: 'Comprar ahora',
        })}
        white={dark}
      />
    </Wrapper>
  );

  const hiddenAddtoCartButton = (
    <Wrapper className="flex justify-between items-center mt-16 w-full">
      <InverseButton
        full
        label="Agregar al carrito"
        size="m"
        onClick={() => handleAddToCart()}
        leftIcon={{
          component: inShoppingCart ? ShoppingCartAdded : ShoppingCartLight,
          size: 24,
        }}
        className="pr-0 pl-0"
      />
    </Wrapper>
  );

  if (isEnrollment) {
    return (
      <StyledButton
        href={classesUrl}
        full
        size="m"
        label={t('GO_TO_COURSE', {
          defaultValue: 'Ir al curso',
        })}
        white={dark}
      />
    );
  }

  if (canEnroll) {
    if (courseType === 'temporal-access') {
      return (
        <ProductsContext.Consumer>
          {({ setBasicDataForm }) => (
            <StyledButton
              full
              className="mt-16"
              onClick={() => {
                if (user && user.originalId) {
                  if (user.phoneNumber) {
                    window.location.href = courseEnrollmentUrl;
                  } else {
                    setBasicDataForm({
                      isOpen: true,
                      redirectUrl: courseEnrollmentUrl,
                    });
                  }
                } else {
                  window.showRegisterModal(
                    courseEnrollmentUrl,
                    {},
                    { shouldRequireExtraData: true },
                  );
                }
              }}
              data-testid="watch-course"
              label="Acceso gratuito por 24 horas"
              white={dark}
            />
          )}
        </ProductsContext.Consumer>
      );
    }

    if (pageType === 'COURSE_ACCESS_PREVIEW' && courseType === 'freemium') {
      return (
        <StyledButton
          href={enrollmentAccessPreviewUrl}
          size="m"
          data-testid="watch-course"
          label={t('TRY_COURSE', {
            defaultValue: '¡Pruébalo ahora!',
          })}
          white={dark}
        />
      );
    }

    if (courseType === 'free') {
      return (
        <StyledButton
          onClick={() => {
            if (onClickEnrollNow) {
              onClickEnrollNow();
            } else {
              window.location.href = isBundle
                ? enrollmentUrl
                : courseEnrollmentUrl;
            }
          }}
          white={dark}
          data-testid="watch-course"
          size="m"
          full
          label={t('SUBSCRIBE_FREE', {
            defaultValue: '¡Inscríbete gratis!',
          })}
        />
      );
    }

    if (courseType === 'membership') {
      return (
        <StyledButton
          className="mt-16"
          full
          size="m"
          white={dark}
          href={isBundle ? enrollmentUrl : courseEnrollmentUrl}
          label="Acceso Premium"
        />
      );
    }

    return (
      <StyledButton
        onClick={() => {
          if (onClickEnrollNow) {
            onClickEnrollNow();
          } else {
            window.location.href = isBundle
              ? enrollmentUrl
              : courseEnrollmentUrl;
          }
        }}
        white={dark}
        data-testid="watch-course"
        size="m"
        full
        label={t('SUBSCRIBE_NOW', {
          defaultValue: '¡Inscríbete ahora!',
        })}
      />
    );
  }

  if (courseType === 'membership') {
    return (
      <StyledButton
        className="mt-16"
        full
        size="m"
        href={url}
        label="Acceso Premium"
        white={dark}
      />
    );
  }

  return showOnlyAddToCardButton ? hiddenAddtoCartButton : styledCart;
};

export default withTranslation('main-menu')(AddToCartMobile);
